import { useState, useRef, useCallback } from 'react';
import { throttle } from 'lodash';
import { lookupApi, ILookupNAICS, ILookupState, ILookupSearchTerm } from '../../../_api';
import { useAuth } from '../../../_auth/useAuth';

const THROTTLE_DELAY = 250;
const MIN_STATE_SEARCH_LENGTH = 2;
const MIN_NAICS_SEARCH_LENGTH = 3;

export const useLookupOptions = () => {
    const { getAccessToken } = useAuth();
    const [naicsOptions, setNaicsOptions] = useState<Array<ILookupNAICS>>([]);
    const [stateOptions, setStateOptions] = useState<Array<ILookupState>>([]);

    const fetchStateOptions = useCallback(async (searchTerm: string): Promise<Array<ILookupState> | void> => {
        const options: ILookupSearchTerm = { search_term: searchTerm };
        const authToken = await getAccessToken();
        
        try {
            const resp = await lookupApi.get_state_lookup(options, authToken);
            if (resp?.success) {
                return resp.data;
            }
            if (resp?.error?.length) {
                // State lookups not found
            }
        } catch (err) {
            // Error fetching State lookups
        }
    }, [getAccessToken]);

    const fetchNaicsOptions = useCallback(async (searchTerm: string): Promise<Array<ILookupNAICS> | void> => {
        const options: ILookupSearchTerm = { search_term: searchTerm };
        const authToken = await getAccessToken();
        
        try {
            const resp = await lookupApi.get_naics_lookup(options, authToken);
            if (resp?.success) {
                return resp.data;
            }
            if (resp?.error?.length) {
                // NAICS lookups not found
            }
        } catch (err) {
            // Error fetching NAICS lookups
        }
    }, [getAccessToken]);

    const stateSearch = useRef(
        throttle(async (searchTerm: unknown) => {
            const term = typeof searchTerm === 'string' ? searchTerm : '';
            if (term.length < MIN_STATE_SEARCH_LENGTH) {
                setStateOptions([]);
            } else {
                setStateOptions(await fetchStateOptions(term) as Array<ILookupState>);
            }
        }, THROTTLE_DELAY)
    ).current;

    const naicsSearch = useRef(
        throttle(async (searchTerm: unknown) => {
            const term = typeof searchTerm === 'string' ? searchTerm : '';
            if (term.length < MIN_NAICS_SEARCH_LENGTH) {
                setNaicsOptions([]);
            } else {
                setNaicsOptions(await fetchNaicsOptions(term) as Array<ILookupNAICS>);
            }
        }, THROTTLE_DELAY)
    ).current;

    return {
        naicsOptions,
        stateOptions,
        stateSearch,
        naicsSearch,
    };
};
